import React, { useCallback } from 'react';
import { styled, css } from '@compiled/react';
import BoardIcon from '@atlaskit/icon/core/board';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import type { Command } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useTransformNavigationCommands } from '../../../utils/transform-navigation-commands/index.tsx';
import type { BoardCommandProps } from './types.tsx';

export const useGetBoardCommand = () => {
	const { transformNavigationCommand } = useTransformNavigationCommands();

	const renderBoardName = useCallback(
		(name: string, containerName?: string, isActive?: boolean) => (
			<BoardNameContainer data-testid="command-palette-common.common.ui.command.board.board-name-container">
				<BoardNameText>{name}</BoardNameText>
				{containerName && <ContainerNameText active={isActive}>{containerName}</ContainerNameText>}
			</BoardNameContainer>
		),
		[],
	);

	const getBoardCommand = useCallback(
		({ board, keywords, section, analytics, ...rest }: BoardCommandProps): Command => {
			const { id, url, name, containerId = 'UNAVAILABLE', containerName } = board;

			return transformNavigationCommand(
				{
					id: `board-${id}`,
					section,
					name,
					keywords,
					components: {
						Name: ({ isActive }) => renderBoardName(name, containerName, isActive),
						LeftIcon: () => (
							<BoardIcon
								color="currentColor"
								label=""
								aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
							/>
						),
					},
					analytics: {
						action: 'boardViewed',
						boardId: id,
						boardContainerId: containerId || '',
						metadata: {
							contentId: id,
							containerId,
							contentType: 'board',
						},
						...analytics,
					},
					...rest,
				},
				{ path: url },
			);
		},
		[renderBoardName, transformNavigationCommand],
	);

	return { getBoardCommand };
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardNameContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardNameText = styled.div({
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 'inherit',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ContainerNameText = styled.div<{ active?: boolean }>(
	{
		font: token('font.body.small'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 'inherit',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ active }) =>
		active
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					color: token('color.text.subtle'),
				})
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					color: token('color.text.subtlest'),
				}),
);
